import { useCallback, useEffect, useRef, useState } from 'react'
import { IconClickClose, IconClickSearch } from '../icon/icon-click'
import { SEARCH_FIELD_PROPS } from './search-field-props'
import './filter.scss'

// How long typing has to pause before the paged grid searches by itself
// (owner, 2026-10-21). Enter and the magnifier search at once.
export const SEARCH_DEBOUNCE_MS = 400

// debounceMs: 0 searches only on Enter or the magnifier, as before.
export const Search = ({ className, value, onChange, onSearch, debounceMs = SEARCH_DEBOUNCE_MS }) => {
  // The search waiting for typing to pause; Enter, the magnifier and the
  // clear button cancel it, so the same words are never searched twice.
  const timer = useRef(null)
  // The words the grid is filtered by. With nothing typed and nothing in
  // force, the magnifier, Enter and ✕ would only reload the same rows (owner
  // 2026-10-30), so they do nothing and look idle. Words set from outside (a
  // search kept in the address) are in force. A ref, so a search still
  // waiting for the pause never reads a stale one.
  const active = useRef(value)
  const typed = useRef(false)
  useEffect(() => {
    if (!typed.current) active.current = value
  }, [value])

  const cancel = () => {
    clearTimeout(timer.current)
    timer.current = null
  }

  useEffect(() => cancel, [])

  const search = words => {
    cancel()
    typed.current = false
    if (words === '' && active.current === '') return
    active.current = words
    onSearch && onSearch(words)
  }

  const handleChange = e => {
    const words = e.target.value
    typed.current = true
    onChange && onChange(words)
    cancel()
    if (debounceMs > 0) {
      timer.current = setTimeout(() => search(words), debounceMs)
    }
  }

  const empty = value === '' && active.current === ''
  const handleClear = () => {
    if (empty) return
    search('')
    onChange && onChange('')
  }

  const handleKeyUp = e => e.code === 'Enter' && search(value)
  const handleSearch = () => search(value)
  const idle = idleWhen => `input-group-text${idleWhen ? ' tb-search-idle' : ''}`

  return (
    <div className={className}>
      <div className="input-group tb-search">
        <input {...SEARCH_FIELD_PROPS} name="grid-search" aria-label="Search" className="form-control search-own-clear" placeholder="Search" value={value} onChange={handleChange} onKeyUp={handleKeyUp} />
        <span className={idle(empty)}>
          <IconClickSearch disabled={empty} onClick={handleSearch} />
        </span>
        <span className={idle(empty)}>
          <IconClickClose disabled={empty} onClick={handleClear} />
        </span>
      </div>
    </div>
  )
}

export const Filter = ({ className, value, onChange }) => {
  const handleChange = e => onChange && onChange(e.target.value)
  const handleSearch = () => onChange && onChange(value)
  const handleClear = () => onChange && onChange('')
  return (
    <div className={className}>
      <div className="input-group tb-search">
        <input {...SEARCH_FIELD_PROPS} name="grid-filter" aria-label="Filter" className="form-control search-own-clear" placeholder="Filter" value={value} onChange={handleChange} />
        <span className="input-group-text">
          <IconClickSearch onClick={handleSearch} />
        </span>
        <span className={`input-group-text${value === '' ? ' tb-search-idle' : ''}`}>
          <IconClickClose disabled={value === ''} onClick={handleClear} />
        </span>
      </div>
    </div>
  )
}

export const useFilter = () => {
  const [state, updateState] = useState('')
  const updateFilter = useCallback(value => updateState(value), [])
  return [state, updateFilter]
}
